/**
 * DOM-based HUD: resource bar, selection panel (train/build/research),
 * anomaly alerts, toasts, scoreboard overlay, game-over overlay. Built once
 * in the constructor, then mutated in place. Heavier rebuilds (selection
 * panel, scoreboard, anomaly list) are throttled to the sim tick rate
 * (8/sec) since that's the fastest their content can actually change.
 */

import {
  BUILDINGS,
  CYBER,
  ECONOMY,
  FACTIONS,
  FRONTIER,
  PHASE_ADVANCE,
  PHASE_NAMES,
  TECHS,
  TICKS_PER_SECOND,
  UNITS,
  activePhaseWork,
  activeResearch,
  activeTrainingRun,
  frontierRunCost,
  buildingRequirementMet,
  canFactionBuild,
  entityCenter,
  findBuildSpot,
  getEntity,
  playerBuildings,
} from '../sim';
// activeFineTune isn't re-exported from the sim barrel (it was added
// alongside the rest of Economy 2.0's sim-internal helpers) -- pull it
// straight from sim.ts rather than touching the barrel under src/sim/**.
import { activeFineTune } from '../sim/sim';
import { ACHIEVEMENTS } from '../shared/achievements';
import type {
  Building,
  BuildingType,
  Doctrine,
  EntityId,
  GameState,
  Stance,
  TechId,
  Unit,
  UnitType,
  Vec2,
} from '../sim';
import type { CyberAbility, GameSession } from './session';
import { fmtClock, renderSummary } from './summary';

/** Max units a Train ×n batch can queue at once (also bounded by the
 *  building's own 5-slot queue — see maxTrainQty). */
const MAX_TRAIN_QTY = 5;
/** Max construction sites a single Quick Build tap can drop at once. */
const MAX_QUICK_BUILD_QTY = 3;

/** One-word role tag per unit type, used on the stat card and appended to train buttons. */
const UNIT_ROLE: Record<UnitType, string> = {
  engineer: 'harvest',
  inferenceAgent: 'economy',
  scoutCrawler: 'scout',
  redTeamAgent: 'raider',
  guardrailSentinel: 'escort',
  loadTester: 'siege',
  frontierAgent: 'apex',
  burstWorker: 'burst',
  reservedInstance: 'prepaid tank',
  distilledAgent: 'budget economy',
  superCluster: 'apex tank',
  spotInstance: 'burst raider',
  forkNode: 'swarm',
};

/** Combat-capable = has an attack stat and isn't the engineer (whose 3/1 stat
 *  is self-defense flavor, not a combat role) — the same units that get a
 *  stance row and auto-engage in the sim (see sim.ts autoAcquire). */
function isCombatCapable(u: Unit): boolean {
  return u.type !== 'engineer' && !!UNITS[u.type].attack;
}

/** Coarse, relative speed callout — not every unit's speed is interesting enough to flag. */
function speedTag(speed: number): string {
  if (speed >= 2.8) return 'fast';
  if (speed <= 1.6) return 'slow';
  return '';
}

/** Appended to a unit-type's stat line when at least one selected instance
 *  has a TTL (Burst Worker): shows the soonest-expiring instance's remaining
 *  lifetime as "⏱ Ns left". */
function ttlSuffix(t: UnitType, instances: Unit[], tick: number): string {
  const us = UNITS[t];
  if (us.ttlSec === undefined) return '';
  const withExpiry = instances.filter((u) => u.expiresTick !== undefined);
  if (withExpiry.length === 0) return '';
  const soonest = Math.min(...withExpiry.map((u) => u.expiresTick!));
  const secsLeft = Math.max(0, Math.ceil((soonest - tick) / TICKS_PER_SECOND));
  return ` · ⏱ ${secsLeft}s left`;
}

/** Compact "role tag + key stats" line shown under each unique selected type's chip. */
function unitStatLine(t: UnitType, instances: Unit[], tick: number): string {
  const us = UNITS[t];
  const role = UNIT_ROLE[t];
  const ttl = ttlSuffix(t, instances, tick);

  if (t === 'loadTester') {
    const towerRange = BUILDINGS.firewall.attack?.range;
    const outrange = towerRange !== undefined ? ` (outranges towers, ${towerRange})` : '';
    return `${role.toUpperCase()} · dmg ${us.attack!.damage} · range ${us.attack!.range}${outrange} · buildings only · slow`;
  }
  if (t === 'scoutCrawler') {
    const bits = [`vision ${us.sight}`];
    if (us.attack) bits.push(`pester dmg ${us.attack.damage}`);
    return bits.join(' · ');
  }
  if (t === 'guardrailSentinel') {
    return `tanky ${role}`;
  }
  if (t === 'burstWorker') {
    const serveTimes = us.servePeriodMult ? Math.round(1 / us.servePeriodMult) : 1;
    return `serves ${serveTimes}x · expires ${us.ttlSec}s${ttl}`;
  }
  if (t === 'reservedInstance') {
    return `dmg ${us.attack!.damage} · range ${us.attack!.range} · tanky · burn $${us.burnPerSec.toFixed(2)}/s`;
  }
  if (t === 'distilledAgent') {
    const revenuePct = Math.round((us.serveRevenueMult ?? 1) * 100);
    return `half inputs · ${revenuePct}% revenue`;
  }

  const bits: string[] = [];
  if (us.attack) {
    bits.push(`dmg ${us.attack.damage}`);
    bits.push(`range ${us.attack.range}`);
  }
  const speed = speedTag(us.speed);
  bits.push(speed ? `${speed} ${role}` : role);
  return bits.join(' · ') + ttl;
}

export class Hud {
  private lastTick = -1;
  private scoreboardOpen = false;
  private gameOverRendered = false;
  private sheetCollapsed = false;
  private recycleArm: { id: number; until: number } | null = null;

  // -- Selection-panel rebuild guard. The panel rebuilds its innerHTML when
  // content changes, but doing so destroys the DOM node mid-gesture and kills
  // momentum scrolling on mobile. So: (1) skip the rewrite entirely when the
  // rendered content is unchanged (idle panels never rebuild), and (2) hold
  // off rewriting while the user is actively touching/scrolling the panel,
  // resuming shortly after they lift off. --
  private lastSelKey = '';
  /** performance.now() until which the panel is considered "being touched". */
  private selInteractUntil = 0;

  // -- Train ×n stepper state. Keyed by "buildingId:unitType" since the
  //    selection panel is rebuilt every sim tick — the quantity has to live
  //    here, not on any DOM node. Reset whenever the selected building
  //    changes (see renderBuildingPanel). --
  private trainQty: Map<string, number> = new Map();
  private trainQtyBuildingId: EntityId | null = null;

  // -- Quick Build stepper + placement stickiness state (see renderBuildMenu
  //    / maybeRearmPlacement). All ephemeral UI state, kept off the DOM for
  //    the same per-tick-rebuild reason as trainQty. --
  private quickBuildQty = 1;
  private quickBuildForBuilding: BuildingType | null = null;
  private repeatPlacement = false;
  private shiftHeld = false;
  private placementWasActive = false;

  private elBudget: HTMLElement;
  private elCashflow: HTMLElement;
  private elTokens: HTMLElement;
  private elTokenFlow: HTMLElement;
  private elData: HTMLElement;
  private elPower: HTMLElement;
  private elPowerWrap: HTMLElement;
  private elDoctrineWrap: HTMLElement;
  private elDoctrineBadge: HTMLElement;
  private elGen: HTMLElement;
  private elPhase: HTMLElement;
  private elInsolvency: HTMLElement;
  private elBrownout: HTMLElement;
  private btnAdvance: HTMLButtonElement;
  private btnChooseDoctrine: HTMLButtonElement;
  private elDoctrineOverlay: HTMLElement;
  private elAnomalyAlerts: HTMLElement;
  private elSelectionPanel: HTMLElement;
  private elScoreboardOverlay: HTMLElement;
  private elScoreboardTable: HTMLElement;
  private elGameOver: HTMLElement;
  private elGameOverTitle: HTMLElement;
  private elGameOverStats: HTMLElement;
  private elGameOverSummary: HTMLElement;
  private elGameOverAch: HTMLElement;
  /** how many session.newAchievements are already rendered on the panel */
  private achShown = 0;

  constructor(
    private hudRoot: HTMLElement,
    private toastRoot: HTMLElement,
    private session: GameSession,
    private onBackToMenu: () => void,
    private onHelp: () => void,
    private onCopilot: () => void,
  ) {
    hudRoot.innerHTML = `
      <div class="resource-bar">
        <div class="res-row">
          <div class="res"><span class="res-label">$</span><span class="res-value" id="res-budget">0</span><span class="res-rate" id="res-cashflow"></span></div>
          <div class="res"><span class="res-label"><span class="lbl-full">Tokens</span><span class="lbl-short">&#9889;</span></span><span class="res-value" id="res-tokens">0/0</span><span class="res-rate" id="res-tokenflow"></span></div>
          <div class="res"><span class="res-label"><span class="lbl-full">Data</span><span class="lbl-short">&#9670;</span></span><span class="res-value" id="res-data">0/0</span></div>
          <div class="res res-power" id="res-power-wrap"><span class="res-label"><span class="lbl-full">Power</span><span class="lbl-short">&#128268;</span></span><span class="res-value" id="res-power">0/0</span></div>
          <div class="res res-doctrine" id="res-doctrine-wrap" hidden><span class="doctrine-badge" id="res-doctrine-badge"></span></div>
          <div class="res"><span class="res-label"><span class="lbl-full">Phase</span><span class="lbl-short">Ph</span></span><span class="res-value" id="res-phase">Crawl</span></div>
          <div class="res" id="res-gen-wrap" title="Your model generation vs the global frontier. Agents behind the frontier earn less per serve."><span class="res-label"><span class="lbl-full">Model</span><span class="lbl-short">Gen</span></span><span class="res-value" id="res-gen">Gen 0</span></div>
        </div>
        <div class="action-row">
          <button class="btn-advance" id="btn-advance-phase"></button>
          <button class="btn-doctrine" id="btn-choose-doctrine" hidden>Choose Doctrine</button>
          <div class="spacer"></div>
          <button class="btn-hud" id="btn-scoreboard"><span class="lbl-full">Scoreboard (Tab)</span><span class="lbl-short">&#127942;</span></button>
          <button class="btn-hud" id="btn-copilot" title="AI Copilot"><span class="lbl-full">&#129302; Copilot</span><span class="lbl-short">&#129302;</span></button>
          <button class="btn-hud" id="btn-help" aria-label="Help" title="Help">?</button>
          <button class="btn-hud" id="btn-menu"><span class="lbl-full">Menu</span><span class="lbl-short">&#9776;</span></button>
        </div>
      </div>
      <div class="status-banners">
        <div class="insolvency-banner" id="insolvency-banner" hidden></div>
        <div class="brownout-banner" id="brownout-banner" hidden>BROWNOUT — output throttled</div>
      </div>
      <div class="anomaly-alerts" id="anomaly-alerts"></div>
      <div class="selection-panel" id="selection-panel"><div class="empty-hint">No selection</div></div>
      <canvas id="minimap" class="minimap" width="180" height="180"></canvas>
      <div class="scoreboard-overlay" id="scoreboard-overlay" hidden>
        <div class="scoreboard-box">
          <h2>Scoreboard</h2>
          <table id="scoreboard-table"></table>
          <button class="btn-hud" id="btn-close-scoreboard">Close</button>
        </div>
      </div>
      <div class="doctrine-overlay" id="doctrine-overlay" hidden>
        <div class="doctrine-box">
          <h2>Choose Your Serving Doctrine</h2>
          <div class="doctrine-intro">Permanent, exclusive — there are no take-backs. Pick the fuel model your economy runs on.</div>
          <div class="doctrine-cards">
            <div class="doctrine-card" data-doctrine="rag">
              <div class="doctrine-name">RAG</div>
              <div class="doctrine-tagline">Data-hungry map control</div>
              <div class="doctrine-desc">${ECONOMY.ragDataCostMult}&times; data cost per serve, +${Math.round((ECONOMY.ragRevenueMult - 1) * 100)}% revenue. Hold the data nodes and out-earn everyone.</div>
            </div>
            <div class="doctrine-card" data-doctrine="finetune">
              <div class="doctrine-name">Fine-Tuning</div>
              <div class="doctrine-tagline">Capital compounding</div>
              <div class="doctrine-desc">${ECONOMY.fineTuneDataCostMult}&times; data cost per serve. Unlocks fine-tuning jobs at the Training Cluster for permanent, stacking revenue.</div>
            </div>
          </div>
          <button class="btn-hud" id="btn-close-doctrine">Cancel</button>
        </div>
      </div>
      <div class="gameover-overlay" id="gameover-overlay" hidden>
        <div class="gameover-box">
          <h1 id="gameover-title"></h1>
          <div id="gameover-stats"></div>
          <div id="gameover-summary"></div>
          <div class="gameover-achievements" id="gameover-achievements"></div>
          <button class="btn-primary" id="btn-back-menu">Back to Menu</button>
        </div>
      </div>
    `;

    this.elBudget = q(hudRoot, '#res-budget');
    this.elCashflow = q(hudRoot, '#res-cashflow');
    this.elTokens = q(hudRoot, '#res-tokens');
    this.elTokenFlow = q(hudRoot, '#res-tokenflow');
    this.elData = q(hudRoot, '#res-data');
    this.elPower = q(hudRoot, '#res-power');
    this.elPowerWrap = q(hudRoot, '#res-power-wrap');
    this.elDoctrineWrap = q(hudRoot, '#res-doctrine-wrap');
    this.elDoctrineBadge = q(hudRoot, '#res-doctrine-badge');
    this.elGen = q(hudRoot, '#res-gen');
    this.elPhase = q(hudRoot, '#res-phase');
    this.elInsolvency = q(hudRoot, '#insolvency-banner');
    this.elBrownout = q(hudRoot, '#brownout-banner');
    this.btnAdvance = q(hudRoot, '#btn-advance-phase') as HTMLButtonElement;
    this.btnChooseDoctrine = q(hudRoot, '#btn-choose-doctrine') as HTMLButtonElement;
    this.elDoctrineOverlay = q(hudRoot, '#doctrine-overlay');
    this.elAnomalyAlerts = q(hudRoot, '#anomaly-alerts');
    this.elSelectionPanel = q(hudRoot, '#selection-panel');
    // A genuine SCROLL or DRAG (not a tap) opens a short grace window during
    // which we won't rebuild the panel's DOM — rebuilding mid-gesture kills
    // momentum scrolling on mobile (see updateSelectionPanel). Taps are NOT
    // suppressed, so buttons/steppers stay instantly responsive. `scroll` is
    // captured because it doesn't bubble from the inner #sel-body.
    const bumpInteract = () => {
      this.selInteractUntil = performance.now() + 600;
    };
    this.elSelectionPanel.addEventListener('scroll', bumpInteract, true);
    this.elSelectionPanel.addEventListener('touchmove', bumpInteract, { passive: true });
    this.elSelectionPanel.addEventListener('pointermove', (e) => {
      if (e.buttons > 0) bumpInteract(); // desktop drag-scroll
    });
    this.elScoreboardOverlay = q(hudRoot, '#scoreboard-overlay');
    this.elScoreboardTable = q(hudRoot, '#scoreboard-table');
    this.elGameOver = q(hudRoot, '#gameover-overlay');
    this.elGameOverTitle = q(hudRoot, '#gameover-title');
    this.elGameOverStats = q(hudRoot, '#gameover-stats');
    this.elGameOverSummary = q(hudRoot, '#gameover-summary');
    this.elGameOverAch = q(hudRoot, '#gameover-achievements');

    this.btnAdvance.addEventListener('click', () => this.session.queueCommand({ type: 'advancePhase' }));
    this.btnChooseDoctrine.addEventListener('click', () => {
      this.elDoctrineOverlay.hidden = false;
    });
    q(hudRoot, '#btn-close-doctrine').addEventListener('click', () => {
      this.elDoctrineOverlay.hidden = true;
    });
    this.elDoctrineOverlay.querySelectorAll<HTMLElement>('.doctrine-card').forEach((card) => {
      card.addEventListener('click', () => {
        const doctrine = card.dataset.doctrine as Doctrine;
        this.session.queueCommand({ type: 'chooseDoctrine', doctrine });
        this.elDoctrineOverlay.hidden = true;
      });
    });
    q(hudRoot, '#btn-scoreboard').addEventListener('click', () => this.toggleScoreboard());
    q(hudRoot, '#btn-copilot').addEventListener('click', () => this.onCopilot());
    q(hudRoot, '#btn-help').addEventListener('click', () => this.onHelp());
    q(hudRoot, '#btn-close-scoreboard').addEventListener('click', () => this.toggleScoreboard());
    q(hudRoot, '#btn-menu').addEventListener('click', () => this.onBackToMenu());
    q(hudRoot, '#btn-back-menu').addEventListener('click', () => this.onBackToMenu());

    // Sticky-placement (desktop): tracked independently of input.ts's own
    // key handling so held-Shift can be read at the moment placement mode
    // exits, without adding a build-mode special case to the input
    // controller itself.
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Shift') this.shiftHeld = true;
    });
    window.addEventListener('keyup', (e) => {
      if (e.key === 'Shift') this.shiftHeld = false;
    });
    window.addEventListener('blur', () => {
      this.shiftHeld = false;
    });
  }

  get minimapCanvas(): HTMLCanvasElement {
    return q(this.hudRoot, '#minimap') as HTMLCanvasElement;
  }

  /** Bounding rect of the bottom selection panel, for the renderer's
   * camera-obstruction handling (overscroll room + nudging a freshly
   * selected unit out from under the panel). Null when it isn't showing. */
  selectionPanelRect(): DOMRect | null {
    const rect = this.elSelectionPanel.getBoundingClientRect();
    return rect.height > 0 ? rect : null;
  }

  toggleScoreboard(): void {
    this.scoreboardOpen = !this.scoreboardOpen;
    this.elScoreboardOverlay.hidden = !this.scoreboardOpen;
    if (this.scoreboardOpen && this.session.state) this.renderScoreboard(this.session.state);
  }

  update(): void {
    const s = this.session.state;
    this.updateResourceBar(s);
    if (!s) return;
    this.maybeRearmPlacement();
    const tickChanged = s.tick !== this.lastTick;
    if (tickChanged) {
      this.lastTick = s.tick;
      this.updateToasts();
      this.updateAnomalyAlerts();
      this.updateSelectionPanel(s);
      if (this.scoreboardOpen) this.renderScoreboard(s);
    }
    this.updateGameOver(s);
  }

  /** Sticky placement: runs every frame (not gated on tickChanged, so it
   *  reacts the instant placement mode exits) watching for the
   *  active-to-inactive transition. If a manual build just landed (tracked
   *  by session.lastManualBuild — see GameSession.queueCommand) and the
   *  player is holding Shift or has the "repeat" toggle on, re-arm placement
   *  with the same building/engineer instead of leaving placement mode. A
   *  plain cancel (Escape / right-click / the Cancel button) never sets
   *  lastManualBuild, so it always exits cleanly regardless of the toggle. */
  private maybeRearmPlacement(): void {
    const activeNow = this.session.placement !== null;
    if (this.placementWasActive && !activeNow) {
      const sticky = this.shiftHeld || this.repeatPlacement;
      const last = this.session.lastManualBuild;
      if (sticky && last) {
        this.session.placement = {
          building: last.building,
          engineerId: last.engineerId,
          cursor: { x: this.session.camera.x + 6, y: this.session.camera.y + 6 },
        };
      }
      this.session.lastManualBuild = null;
    }
    this.placementWasActive = this.session.placement !== null;
  }

  private updateResourceBar(s: GameState | null): void {
    if (!s) return;
    const p = s.players[this.session.myPlayer];
    this.elBudget.textContent = `$${Math.round(p.budget).toLocaleString()}`;
    const net = p.revenuePerSec - p.burnPerSec;
    this.elCashflow.textContent = `${net >= 0 ? '+' : ''}${net.toFixed(1)}/s`;
    this.elCashflow.className = `res-rate ${net >= 0 ? 'positive' : 'negative'}`;
    this.elTokens.textContent = `${Math.round(p.tokens)}/${Math.round(p.tokenCap)}`;
    this.elTokenFlow.textContent = `${p.tokensPerSec >= 0 ? '+' : ''}${p.tokensPerSec.toFixed(1)}/s`;
    this.elTokenFlow.className = `res-rate ${p.tokensPerSec >= 0 ? 'positive' : 'negative'}`;

    this.elData.textContent = `${Math.round(p.data)}/${Math.round(p.dataCap)}`;
    // Amber warning once data is thin enough that staleness looms (serving
    // drains data every tick; below ~15% of cap a couple more serves empties
    // the pool and revenue drops to the stale-model floor).
    const lowData = p.dataCap > 0 && p.data < p.dataCap * 0.15;
    this.elData.className = `res-value ${lowData ? 'low-data' : ''}`;

    const brownedOut = p.powerDraw > p.powerCap;
    this.elPower.textContent = `${Math.round(p.powerDraw)}/${Math.round(p.powerCap)}`;
    this.elPowerWrap.classList.toggle('brownout', brownedOut);
    this.elBrownout.hidden = !brownedOut;

    if (p.doctrine) {
      this.elDoctrineWrap.hidden = false;
      this.elDoctrineBadge.textContent = p.doctrine === 'rag' ? 'RAG' : 'FINE-TUNE';
      this.elDoctrineBadge.className = `doctrine-badge doctrine-${p.doctrine}`;
    } else {
      this.elDoctrineWrap.hidden = true;
    }

    const finopsOperational = playerBuildings(s, this.session.myPlayer).some(
      (b) => b.type === 'finopsCenter' && b.buildTicks === 0,
    );
    this.btnChooseDoctrine.hidden = !(p.doctrine === null && p.phase >= 1 && finopsOperational);

    this.elPhase.textContent = PHASE_NAMES[p.phase];

    // Model generation vs the global frontier: "Gen 1" at the frontier,
    // "Gen 1 / 2" (amber) when a rival's launch has repriced your fleet.
    const behind = s.frontierGen > p.modelGen;
    this.elGen.textContent = behind ? `Gen ${p.modelGen} / ${s.frontierGen}` : `Gen ${p.modelGen}`;
    this.elGen.className = `res-value ${behind ? 'gen-behind' : ''}`;

    if (p.phase >= 2) {
      this.btnAdvance.hidden = true;
    } else {
      this.btnAdvance.hidden = false;
      const ps = PHASE_ADVANCE[p.phase];
      const work = activePhaseWork(s, this.session.myPlayer);
      if (work) {
        const total = Math.max(1, Math.round(ps.timeSec * TICKS_PER_SECOND));
        const pct = Math.round(100 * (1 - work.ticksLeft / total));
        this.btnAdvance.textContent = `Advancing to ${ps.name}… ${pct}%`;
        this.btnAdvance.disabled = true;
      } else {
        const afford = p.budget >= ps.costBudget && p.data >= ps.costData;
        this.btnAdvance.textContent = `Advance to ${ps.name} ($${ps.costBudget} + ${ps.costData} data)`;
        this.btnAdvance.disabled = !afford;
      }
    }

    if (p.budget <= 0) {
      const graceTicks = Math.round(ECONOMY.insolvencyGraceSec * TICKS_PER_SECOND);
      const leftSec = Math.max(0, graceTicks - p.insolvencyTicks) / TICKS_PER_SECOND;
      this.elInsolvency.hidden = false;
      this.elInsolvency.textContent = `INSOLVENT — ${leftSec.toFixed(1)}s of runway before bankruptcy`;
    } else {
      this.elInsolvency.hidden = true;
    }
  }

  private updateToasts(): void {
    // Anomaly toasts always render on top of the stack, copilot advice next;
    // stable sort keeps everything else in original (insertion) order.
    const rank = (t: { anomaly?: boolean; copilot?: boolean }) => (t.anomaly ? 2 : t.copilot ? 1 : 0);
    const ordered = [...this.session.toasts].sort((a, b) => rank(b) - rank(a));
    this.toastRoot.innerHTML = ordered
      .map(
        (t) =>
          `<div class="toast toast-${t.kind}${t.anomaly ? ' toast-anomaly' : ''}${t.copilot ? ' toast-copilot' : ''}">${escapeHtml(t.message)}${
            t.count > 1 ? `<span class="toast-count">&times;${t.count}</span>` : ''
          }</div>`,
      )
      .join('');
  }

  private updateAnomalyAlerts(): void {
    const ids = Array.from(this.session.activeAnomalies);
    if (ids.length === 0) {
      this.elAnomalyAlerts.innerHTML = '';
      return;
    }
    this.elAnomalyAlerts.innerHTML = ids
      .map(
        (id) => `
      <div class="anomaly-banner">
        <span>Cost anomaly: a runaway agent is burning tokens and earning nothing.</span>
        <button class="btn-terminate" data-unit="${id}">Terminate</button>
      </div>`,
      )
      .join('');
    this.elAnomalyAlerts.querySelectorAll<HTMLButtonElement>('.btn-terminate').forEach((btn) => {
      btn.addEventListener('click', () => {
        const unitId = Number(btn.dataset.unit);
        this.session.queueCommand({ type: 'fixAnomaly', unitId });
      });
    });
  }

  // -------------------------------------------------------------------
  // Selection panel
  // -------------------------------------------------------------------

  private updateSelectionPanel(s: GameState): void {
    const me = this.session.myPlayer;
    const live = this.session.selection
      .map((id) => getEntity(s, id))
      .filter((e): e is Building | Unit => !!e && e.owner === me && (e.kind === 'building' || e.kind === 'unit'));
    this.session.selection = live.map((e) => e.id);

    const container = this.elSelectionPanel;
    if (live.length === 0) {
      container.hidden = true;
      container.innerHTML = '';
      this.sheetCollapsed = false;
      this.lastSelKey = '';
      return;
    }
    container.hidden = false;

    const buildingsSel = live.filter((e): e is Building => e.kind === 'building');
    const unitsSel = live.filter((e): e is Unit => e.kind === 'unit');

    let bodyHtml: string;
    let summary: string;
    if (buildingsSel.length === 1 && unitsSel.length === 0) {
      const b = buildingsSel[0];
      bodyHtml = this.renderBuildingPanel(s, b);
      summary = BUILDINGS[b.type].name;
    } else {
      bodyHtml = this.renderUnitPanel(s, unitsSel, buildingsSel);
      summary = this.summaryLabel(unitsSel, buildingsSel);
    }

    // Skip the DOM rebuild unless the rendered content actually changed. A
    // static panel (no live queue/cooldown/progress) then never rebuilds, so
    // it scrolls freely. When content does change, still hold off while the
    // user is touching the panel — rebuilding mid-gesture destroys the node
    // and kills momentum scrolling on mobile. The change lands once they
    // lift off (lastSelKey isn't updated, so the next idle tick applies it).
    const key = `${this.sheetCollapsed ? 'c' : 'o'} ${summary} ${bodyHtml}`;
    if (key === this.lastSelKey) return;
    if (performance.now() < this.selInteractUntil) return;
    this.lastSelKey = key;

    // Carry the scroll position across the rebuild as a final safety net for
    // any rebuild that lands between gestures.
    const prevScrollTop = container.querySelector('#sel-body')?.scrollTop ?? 0;

    container.innerHTML = `
      <div class="sel-header" id="sel-header">
        <span class="sel-summary">${escapeHtml(summary)}</span>
        <span class="sel-collapse-indicator">${this.sheetCollapsed ? '▲' : '▼'}</span>
        <button class="sel-close-btn" id="sel-close-btn" aria-label="Clear selection" title="Clear selection (Esc)">&#10005;</button>
      </div>
      <div class="sel-body" id="sel-body">${bodyHtml}</div>
    `;
    container.classList.toggle('collapsed', this.sheetCollapsed);
    q(container, '#sel-body').scrollTop = prevScrollTop;

    q(container, '#sel-close-btn').addEventListener('click', (ev) => {
      ev.stopPropagation();
      this.session.selection = [];
      this.sheetCollapsed = false;
    });
    q(container, '#sel-header').addEventListener('click', () => {
      this.sheetCollapsed = !this.sheetCollapsed;
      container.classList.toggle('collapsed', this.sheetCollapsed);
      const indicator = container.querySelector('.sel-collapse-indicator');
      if (indicator) indicator.textContent = this.sheetCollapsed ? '▲' : '▼';
    });

    if (buildingsSel.length === 1 && unitsSel.length === 0) {
      this.wireBuildingPanel(buildingsSel[0]);
    } else {
      this.wireUnitPanel(unitsSel);
    }
  }

  private summaryLabel(unitsSel: Unit[], buildingsSel: Building[]): string {
    const total = unitsSel.length + buildingsSel.length;
    if (unitsSel.length > 0 && buildingsSel.length === 0) {
      const counts = new Map<UnitType, number>();
      for (const u of unitsSel) counts.set(u.type, (counts.get(u.type) ?? 0) + 1);
      if (counts.size === 1) {
        const [[t, c]] = Array.from(counts.entries());
        return `${UNITS[t].name} ×${c}`;
      }
    }
    return `Selection ×${total}`;
  }

  private renderBuildingPanel(s: GameState, b: Building): string {
    const p = s.players[this.session.myPlayer];
    const bs = BUILDINGS[b.type];
    let html = `<div class="hp-line">HP ${Math.round(b.hp)}/${b.maxHp}</div>`;
    if (b.buildTicks > 0) {
      const total = Math.max(1, Math.round(bs.buildTimeSec * TICKS_PER_SECOND));
      const pct = Math.round(100 * (1 - b.buildTicks / total));
      html += `<div class="construction">Constructing… ${pct}%</div>`;
      html += this.renderRecycleButton(s, b);
      return html;
    }

    // Reset every stepper's remembered quantity the moment the selected
    // building changes — a leftover "×4" from a different Training Cluster
    // makes no sense once you've clicked over to the HQ.
    if (this.trainQtyBuildingId !== b.id) {
      this.trainQty.clear();
      this.trainQtyBuildingId = b.id;
    }

    // Faction-exclusive units (UNITS[t].faction) are hidden outright for
    // every other faction — they're not part of that faction's world, not
    // just disabled.
    const trainable = bs.trains.filter((ut) => !UNITS[ut].faction || UNITS[ut].faction === p.faction);
    if (trainable.length > 0) {
      html += `<div class="section-label">Train</div><div class="train-list">`;
      const roomLeft = Math.max(0, MAX_TRAIN_QTY - b.queue.length);
      for (const ut of trainable) {
        const us = UNITS[ut];
        let cost = Math.round(us.costBudget * FACTIONS[p.faction].costMult);
        if (p.techs.includes('modelRouting')) cost = Math.round(cost * 0.85);
        let dataCost = us.costData;
        if (p.faction === 'openweights' && us.phase >= 1) dataCost += 10;

        // One control per unit: an info header (name + per-unit cost) and a
        // single action row — a quantity stepper plus one Train button that
        // queues that many. (Previously the header was itself a "train 1"
        // button AND there was a separate Train ×n button, which read as two
        // competing ways to do the same thing.)
        const key = `${b.id}:${ut}`;
        const qty = Math.min(Math.max(1, this.trainQty.get(key) ?? 1), Math.max(1, roomLeft));
        this.trainQty.set(key, qty);
        const affordN =
          roomLeft >= qty && p.phase >= us.phase && p.budget >= cost * qty && p.data >= dataCost * qty;
        const stepDisabled = roomLeft === 0;
        const trainLabel = qty > 1 ? `Train &times;${qty}` : 'Train';
        html += `<div class="train-item${affordN ? '' : ' train-item-off'}" title="${escapeHtml(us.desc)}">
          <div class="train-item-head">
            <span class="btn-item-name">${us.name} <span class="btn-item-role">— ${UNIT_ROLE[ut]}</span></span>
            <span class="btn-item-cost">$${cost}${dataCost ? ` + ${dataCost} data` : ''}</span>
          </div>
          <div class="train-qty-row">
            <div class="qty-stepper">
              <button class="qty-btn qty-minus" data-unit="${ut}" aria-label="Fewer ${escapeHtml(us.name)}" ${stepDisabled ? 'disabled' : ''}>&minus;</button>
              <span class="qty-value">${qty}</span>
              <button class="qty-btn qty-plus" data-unit="${ut}" aria-label="More ${escapeHtml(us.name)}" ${stepDisabled || qty >= roomLeft ? 'disabled' : ''}>+</button>
            </div>
            <button class="btn-small btn-train-n" data-unit="${ut}" ${affordN ? '' : 'disabled'} title="Queue ${qty} ${escapeHtml(us.name)}">${trainLabel}</button>
          </div>
        </div>`;
      }
      html += `</div>`;
      if (b.queue.length > 0) {
        const first = b.queue[0];
        const total = Math.max(
          1,
          Math.round(UNITS[first].trainTimeSec * FACTIONS[p.faction].buildSpeedMult * TICKS_PER_SECOND),
        );
        const pct = Math.round(100 * (1 - b.trainTicks / total));
        html += `<div class="queue">Queue: ${b.queue.map((u) => UNITS[u].name).join(', ')} (${pct}%)</div>`;
        html += `<button class="btn-hud" id="btn-cancel-train">Cancel last</button>`;
      }
    }

    if (b.type === 'finopsCenter' || b.type === 'inferenceEndpoint' || b.type === 'socCenter') {
      html += this.renderResearchPanel(s, b.type);
    }
    if (b.type === 'trainingCluster') {
      html += this.renderFrontierPanel(s, b);
      html += this.renderFineTunePanel(s, b);
    }
    if (b.type === 'adversarialLab') {
      html += this.renderCyberPanel(s);
    }
    html += this.renderRecycleButton(s, b);
    return html;
  }

  /** Cyber-offense buttons for an operational Adversarial Lab. Each arms a
   *  targeting mode (session.cyberTarget); the next map click on an enemy
   *  building fires it (see input.ts). Buttons disable while on cooldown or
   *  unaffordable. */
  private renderCyberPanel(s: GameState): string {
    const p = s.players[this.session.myPlayer];
    const ops: { ability: CyberAbility; name: string; phase: number; costBudget: number; costData: number; cd: number; desc: string }[] = [
      {
        ability: 'promptInjection',
        name: 'Prompt Injection',
        phase: CYBER.promptInjection.phase,
        costBudget: CYBER.promptInjection.costBudget,
        costData: CYBER.promptInjection.costData,
        cd: p.cyber.injectCd,
        desc: `Jailbreak up to ${CYBER.promptInjection.maxTargets} enemy agents near the target into runaway loops.`,
      },
      {
        ability: 'ddos',
        name: 'DDoS',
        phase: CYBER.ddos.phase,
        costBudget: CYBER.ddos.costBudget,
        costData: CYBER.ddos.costData,
        cd: p.cyber.ddosCd,
        desc: `Knock the target building offline for ${CYBER.ddos.durationSec}s.`,
      },
      {
        ability: 'dataPoison',
        name: 'Data Poisoning',
        phase: CYBER.dataPoison.phase,
        costBudget: CYBER.dataPoison.costBudget,
        costData: CYBER.dataPoison.costData,
        cd: p.cyber.poisonCd,
        desc: `Scrub ${CYBER.dataPoison.drainData} enemy data and depress their revenue for ${CYBER.dataPoison.durationSec}s.`,
      },
    ];
    let html = `<div class="section-label">Cyber Ops</div><div class="btn-grid">`;
    for (const op of ops) {
      const lockedPhase = p.phase < op.phase;
      const onCd = op.cd > 0;
      const afford = p.budget >= op.costBudget && p.data >= op.costData;
      const armed = this.session.cyberTarget === op.ability;
      const disabled = lockedPhase || onCd || !afford;
      const status = lockedPhase
        ? `Requires ${PHASE_NAMES[op.phase]}`
        : onCd
          ? `Cooldown ${Math.ceil(op.cd / TICKS_PER_SECOND)}s`
          : `$${op.costBudget}${op.costData ? ` + ${op.costData} data` : ''}`;
      html += `<button class="btn-item cyber-btn ${armed ? 'cyber-armed' : ''}" data-ability="${op.ability}" title="${escapeHtml(op.desc)}" ${disabled ? 'disabled' : ''}>
        <span class="btn-item-name">${op.name}${armed ? ' — pick target' : ''}</span>
        <span class="btn-item-cost">${status}</span>
      </button>`;
    }
    html += `</div>`;
    return html;
  }

  /** Two-click demolish: first click arms ("Confirm?"), second sends. The
   * armed flag lives on the Hud (not the DOM) because the panel is rebuilt
   * every sim tick. */
  private renderRecycleButton(s: GameState, b: Building): string {
    if (b.type === 'hq') return ''; // you don't sell the Cloud Console
    const p = s.players[this.session.myPlayer];
    const bs = BUILDINGS[b.type];
    const paid = Math.round(bs.costBudget * FACTIONS[p.faction].costMult);
    const refund =
      b.buildTicks > 0
        ? Math.round(paid * 0.75)
        : Math.round(paid * 0.5 * Math.max(0, b.hp / b.maxHp));
    const armed =
      this.recycleArm !== null &&
      this.recycleArm.id === b.id &&
      performance.now() < this.recycleArm.until;
    const label = armed ? `Confirm demolish? (+$${refund})` : `♻ Recycle (+$${refund})`;
    return `<button class="btn-hud recycle-btn ${armed ? 'recycle-armed' : ''}" title="Demolish for a partial refund. Frees its power draw.">${label}</button>`;
  }

  /** Frontier training run controls for an operational Training Cluster
   *  (Economy 3.0). Shows the escalating cost of the NEXT generation, a
   *  fast-follow tag when a rival already shipped it, and run progress. */
  private renderFrontierPanel(s: GameState, b: Building): string {
    const p = s.players[this.session.myPlayer];
    const active = activeTrainingRun(s, this.session.myPlayer);
    const cost = frontierRunCost(s, p);
    let html = `<div class="section-label">Frontier Race — Gen ${p.modelGen}${
      s.frontierGen > p.modelGen ? ` (frontier: ${s.frontierGen})` : ' (at the frontier)'
    }</div>`;

    let reason = '';
    if (active && active.buildingId === b.id) {
      const total = Math.max(1, Math.round(FRONTIER.runTimeSec * TICKS_PER_SECOND));
      const pct = Math.round(100 * (1 - active.ticksLeft / total));
      html += `<div class="researching">Training Gen ${cost.targetGen}… ${pct}%</div>`;
      reason = `In progress… ${pct}%`;
    } else if (active) {
      reason = 'A training run is already active';
    } else if (p.budget < cost.budget || p.data < cost.data || p.tokens < cost.tokens) {
      reason = 'Insufficient funds';
    }

    const disabled = !!reason;
    const costStr = `$${cost.budget} + ${cost.data} data${cost.tokens ? ` + ${cost.tokens} tokens` : ''}`;
    const followTag = cost.fastFollow ? ' (fast-follow discount)' : '';
    html += `<button class="btn-item frontier-run-btn" data-building="${b.id}" title="Ship Model Gen ${cost.targetGen}${followTag}: reprices older agents game-wide, unlocks premium-market pricing${
      p.doctrine === 'finetune' ? ', RESETS fine-tune bonuses' : ''
    }." ${disabled ? 'disabled' : ''}>
      <span class="btn-item-name">Training Run → Gen ${cost.targetGen}${cost.fastFollow ? ' ⚡' : ''}</span>
      <span class="btn-item-cost">${reason || costStr}</span>
    </button>`;
    return html;
  }

  private renderFineTunePanel(s: GameState, b: Building): string {
    const p = s.players[this.session.myPlayer];
    const active = activeFineTune(s, this.session.myPlayer);
    let html = `<div class="section-label">Fine-Tuning</div>`;

    let reason = '';
    if (p.doctrine !== 'finetune') {
      reason = 'Requires Fine-Tuning doctrine';
    } else if (p.fineTuneJobs >= ECONOMY.fineTuneMaxJobs) {
      reason = `Maxed (${ECONOMY.fineTuneMaxJobs}/${ECONOMY.fineTuneMaxJobs})`;
    } else if (active && active.buildingId === b.id) {
      const total = Math.max(1, Math.round(ECONOMY.fineTuneTimeSec * TICKS_PER_SECOND));
      const pct = Math.round(100 * (1 - active.ticksLeft / total));
      html += `<div class="researching">Fine-tuning… ${pct}%</div>`;
      reason = `In progress… ${pct}%`;
    } else if (active) {
      reason = 'A fine-tuning job is already active';
    } else if (p.data < ECONOMY.fineTuneCostData || p.tokens < ECONOMY.fineTuneCostTokens) {
      reason = 'Insufficient funds';
    }

    const disabled = !!reason;
    const costStr = `${ECONOMY.fineTuneCostData} data + ${ECONOMY.fineTuneCostTokens} tokens`;
    html += `<button class="btn-item fine-tune-btn" data-building="${b.id}" title="Permanent +${Math.round(
      ECONOMY.fineTuneRevenueBonus * 100,
    )}% revenue per job (max ${ECONOMY.fineTuneMaxJobs})" ${disabled ? 'disabled' : ''}>
      <span class="btn-item-name">Fine-Tune Job (${p.fineTuneJobs}/${ECONOMY.fineTuneMaxJobs})</span>
      <span class="btn-item-cost">${reason || costStr}</span>
    </button>`;
    return html;
  }

  private wireBuildingPanel(b: Building): void {
    const roomLeft = Math.max(0, MAX_TRAIN_QTY - b.queue.length);
    this.elSelectionPanel.querySelectorAll<HTMLButtonElement>('.qty-minus').forEach((btn) => {
      btn.addEventListener('click', () => {
        const ut = btn.dataset.unit as UnitType;
        const key = `${b.id}:${ut}`;
        const qty = this.trainQty.get(key) ?? 1;
        this.trainQty.set(key, Math.max(1, qty - 1));
      });
    });
    this.elSelectionPanel.querySelectorAll<HTMLButtonElement>('.qty-plus').forEach((btn) => {
      btn.addEventListener('click', () => {
        const ut = btn.dataset.unit as UnitType;
        const key = `${b.id}:${ut}`;
        const qty = this.trainQty.get(key) ?? 1;
        this.trainQty.set(key, Math.min(Math.max(1, roomLeft), qty + 1));
      });
    });
    this.elSelectionPanel.querySelectorAll<HTMLButtonElement>('.btn-train-n').forEach((btn) => {
      btn.addEventListener('click', () => {
        const ut = btn.dataset.unit as UnitType;
        const key = `${b.id}:${ut}`;
        const qty = Math.min(Math.max(1, this.trainQty.get(key) ?? 1), Math.max(1, roomLeft));
        for (let i = 0; i < qty; i++) {
          this.session.queueCommand({ type: 'train', buildingId: b.id, unit: ut });
        }
        this.trainQty.set(key, 1);
      });
    });
    const cancelBtn = this.elSelectionPanel.querySelector<HTMLButtonElement>('#btn-cancel-train');
    cancelBtn?.addEventListener('click', () => this.session.queueCommand({ type: 'cancelTrain', buildingId: b.id }));
    this.wireResearchButtons();
    const fineTuneBtn = this.elSelectionPanel.querySelector<HTMLButtonElement>('.fine-tune-btn');
    fineTuneBtn?.addEventListener('click', () => this.session.queueCommand({ type: 'fineTune', buildingId: b.id }));
    const frontierBtn = this.elSelectionPanel.querySelector<HTMLButtonElement>('.frontier-run-btn');
    frontierBtn?.addEventListener('click', () => this.session.queueCommand({ type: 'trainingRun', buildingId: b.id }));
    this.elSelectionPanel.querySelectorAll<HTMLButtonElement>('.cyber-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const ability = btn.dataset.ability as CyberAbility;
        // Toggle: clicking the armed ability again disarms it.
        this.session.cyberTarget = this.session.cyberTarget === ability ? null : ability;
      });
    });
    const recycleBtn = this.elSelectionPanel.querySelector<HTMLButtonElement>('.recycle-btn');
    recycleBtn?.addEventListener('click', () => {
      const armed =
        this.recycleArm !== null &&
        this.recycleArm.id === b.id &&
        performance.now() < this.recycleArm.until;
      if (armed) {
        this.session.queueCommand({ type: 'recycle', buildingId: b.id });
        this.recycleArm = null;
        this.session.selection = [];
      } else {
        this.recycleArm = { id: b.id, until: performance.now() + 3000 };
      }
    });
  }

  private renderResearchPanel(s: GameState, lab: BuildingType): string {
    const p = s.players[this.session.myPlayer];
    const active = activeResearch(s, this.session.myPlayer, lab);
    let html = `<div class="section-label">Research</div>`;
    if (active) {
      const total = Math.max(1, Math.round(TECHS[active.tech].researchTimeSec * TICKS_PER_SECOND));
      const pct = Math.round(100 * (1 - active.ticksLeft / total));
      html += `<div class="researching">Researching ${TECHS[active.tech].name}… ${pct}%</div>`;
    }
    html += `<div class="btn-grid">`;
    for (const tid of Object.keys(TECHS) as TechId[]) {
      const t = TECHS[tid];
      if (t.lab !== lab) continue; // this lab's research track only
      if (t.notFaction && t.notFaction === p.faction) continue; // innate
      const done = p.techs.includes(tid);
      const lockedPhase = p.phase < t.phase;
      const lockedReq = t.requires ? !p.techs.includes(t.requires) : false;
      const afford = p.budget >= t.costBudget && p.data >= t.costData;
      const disabled = done || lockedPhase || lockedReq || !!active || !afford;
      const status = done
        ? 'Researched'
        : lockedPhase
          ? `Requires ${PHASE_NAMES[t.phase]}`
          : lockedReq && t.requires
            ? `Requires ${TECHS[t.requires].name}`
            : `$${t.costBudget} + ${t.costData} data`;
      html += `<button class="btn-item research-btn" data-tech="${tid}" title="${escapeHtml(t.desc)}" ${disabled ? 'disabled' : ''}>
        <span class="btn-item-name">${t.name}</span>
        <span class="btn-item-cost">${status}</span>
      </button>`;
    }
    html += `</div>`;
    return html;
  }

  private wireResearchButtons(): void {
    this.elSelectionPanel.querySelectorAll<HTMLButtonElement>('.research-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.session.queueCommand({ type: 'research', tech: btn.dataset.tech as TechId });
      });
    });
  }

  private renderUnitPanel(s: GameState, unitsSel: Unit[], buildingsSel: Building[]): string {
    let html = '';
    if (unitsSel.length > 0) {
      const counts = new Map<UnitType, number>();
      const byType = new Map<UnitType, Unit[]>();
      for (const u of unitsSel) {
        counts.set(u.type, (counts.get(u.type) ?? 0) + 1);
        if (!byType.has(u.type)) byType.set(u.type, []);
        byType.get(u.type)!.push(u);
      }
      html += `<div class="unit-list">`;
      for (const [t, c] of counts) {
        html += `<div class="unit-chip-block">
          <div class="unit-chip">${UNITS[t].name} ×${c}</div>
          <div class="unit-stat-line">${escapeHtml(unitStatLine(t, byType.get(t) ?? [], s.tick))}</div>
        </div>`;
      }
      html += `</div>`;
      html += this.renderStanceRow(unitsSel);
    }
    for (const b of buildingsSel) {
      html += `<div class="mini-building-note">${BUILDINGS[b.type].name}${b.buildTicks > 0 ? ' (constructing)' : ''}</div>`;
    }
    const hasEngineer = unitsSel.some((u) => u.type === 'engineer');
    if (hasEngineer) {
      html += this.renderBuildMenu(s);
    }
    return html;
  }

  /** Aggressive/Defend/Passive toggle row, shown only when the selection
   *  includes at least one combat-capable unit. Highlights the current
   *  stance only when every combat-capable unit selected shares one. */
  private renderStanceRow(unitsSel: Unit[]): string {
    const combat = unitsSel.filter(isCombatCapable);
    if (combat.length === 0) return '';
    const stances = new Set<Stance>(combat.map((u) => u.stance ?? 'aggressive'));
    const common: Stance | null = stances.size === 1 ? [...stances][0] : null;
    const btn = (stance: Stance, icon: string, label: string) =>
      `<button class="stance-btn ${common === stance ? 'active' : ''}" data-stance="${stance}" title="${label} stance">
        <span class="stance-icon">${icon}</span><span class="stance-label">${label}</span>
      </button>`;
    const pending = this.session.pendingOrder;
    const orderBtn = (kind: 'guard' | 'patrol', icon: string, label: string, hint: string) =>
      `<button class="stance-btn order-btn ${pending?.kind === kind ? 'active' : ''}" data-order="${kind}" title="${hint}">
        <span class="stance-icon">${icon}</span><span class="stance-label">${label}</span>
      </button>`;
    return `
      <div class="section-label">Stance</div>
      <div class="stance-row">
        ${btn('aggressive', '&#9876;', 'Aggressive')}
        ${btn('defend', '&#128737;', 'Defend')}
        ${btn('passive', '&#9995;', 'Passive')}
      </div>
      <div class="section-label">Orders</div>
      <div class="stance-row">
        ${orderBtn('guard', '&#128737;&#65039;', 'Guard', 'Follow and protect a friendly unit or building — then click your target')}
        ${orderBtn('patrol', '&#128257;', 'Patrol', 'Patrol between two points, engaging threats — then click two map points')}
      </div>`;
  }

  private renderBuildMenu(s: GameState): string {
    const me = this.session.myPlayer;
    const p = s.players[me];
    let html = `<div class="section-label">Build</div><div class="btn-grid">`;
    for (const bt of Object.keys(BUILDINGS) as BuildingType[]) {
      // Buildings the faction can't ever construct (e.g. serverless + GPU
      // Cluster) are hidden entirely rather than shown disabled.
      if (!canFactionBuild(p.faction, bt)) continue;
      const bs = BUILDINGS[bt];
      const cost = Math.round(bs.costBudget * FACTIONS[p.faction].costMult);
      const lockedPhase = p.phase < bs.phase;
      const lockedReq = !!bs.requires && !buildingRequirementMet(s, me, bt);
      const afford = p.budget >= cost && p.data >= bs.costData;
      const disabled = lockedPhase || lockedReq || !afford;
      const costStr = `$${cost}${bs.costData ? ` + ${bs.costData} data` : ''}`;
      const status = lockedPhase
        ? `Requires ${PHASE_NAMES[bs.phase]}`
        : lockedReq && bs.requires
          ? `Requires ${BUILDINGS[bs.requires].name}`
          : costStr;
      const powerBits: string[] = [];
      if (bs.powerDraw > 0) powerBits.push(`&#9889;${bs.powerDraw}`);
      if (bs.powerCapAdd > 0) powerBits.push(`+${bs.powerCapAdd}&#128268;`);
      const powerStr = powerBits.length ? ` · ${powerBits.join(' ')}` : '';
      html += `<button class="btn-item build-btn" data-building="${bt}" title="${escapeHtml(bs.desc)}" ${disabled ? 'disabled' : ''}>
        <span class="btn-item-name">${bs.name}</span>
        <span class="btn-item-cost">${status}${powerStr}</span>
      </button>`;
    }
    html += `</div>`;
    if (this.session.placement) {
      const placement = this.session.placement;
      const pbs = BUILDINGS[placement.building];
      const name = pbs.name;

      // Quick Build's own stepper resets whenever the picked building
      // changes, mirroring the Train stepper's per-selection reset.
      if (this.quickBuildForBuilding !== placement.building) {
        this.quickBuildQty = 1;
        this.quickBuildForBuilding = placement.building;
      }
      const qbCost = Math.round(pbs.costBudget * FACTIONS[p.faction].costMult);
      const qbRoom = MAX_QUICK_BUILD_QTY;
      const qbQty = Math.min(Math.max(1, this.quickBuildQty), qbRoom);
      this.quickBuildQty = qbQty;
      const qbAfford = p.budget >= qbCost * qbQty && p.data >= pbs.costData * qbQty;

      html += `<div class="placement-hint">
        Placing ${name} — click to place; on touch, tap to position then tap again to confirm.
        <div class="placement-actions">
          <button class="btn-small cancel-placement-btn">Cancel</button>
          <label class="repeat-toggle-label">
            <input type="checkbox" class="repeat-toggle-cb" ${this.repeatPlacement ? 'checked' : ''}>
            Repeat
          </label>
        </div>
        <div class="quick-build-row">
          <div class="qty-stepper">
            <button class="qty-btn qb-minus" aria-label="Fewer Quick Build sites" ${qbQty <= 1 ? 'disabled' : ''}>&minus;</button>
            <span class="qty-value">${qbQty}</span>
            <button class="qty-btn qb-plus" aria-label="More Quick Build sites" ${qbQty >= qbRoom ? 'disabled' : ''}>+</button>
          </div>
          <button class="btn-small quick-build-btn" ${qbAfford ? '' : 'disabled'} title="Auto-place near your HQ — no map tap needed">
            &#9889; Quick Build${qbQty > 1 ? ` &times;${qbQty}` : ''}
          </button>
        </div>
      </div>`;
    }
    return html;
  }

  private wireUnitPanel(unitsSel: Unit[]): void {
    const engineer = unitsSel.find((u) => u.type === 'engineer');
    this.elSelectionPanel
      .querySelector<HTMLButtonElement>('.cancel-placement-btn')
      ?.addEventListener('click', () => {
        this.session.placement = null;
      });
    this.elSelectionPanel.querySelectorAll<HTMLButtonElement>('.build-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!engineer) return;
        const building = btn.dataset.building as BuildingType;
        this.session.placement = {
          building,
          engineerId: engineer.id,
          cursor: { x: this.session.camera.x + 6, y: this.session.camera.y + 6 },
        };
      });
    });
    this.elSelectionPanel.querySelector<HTMLInputElement>('.repeat-toggle-cb')?.addEventListener('change', (ev) => {
      this.repeatPlacement = (ev.target as HTMLInputElement).checked;
    });
    this.elSelectionPanel.querySelector<HTMLButtonElement>('.qb-minus')?.addEventListener('click', () => {
      this.quickBuildQty = Math.max(1, this.quickBuildQty - 1);
    });
    this.elSelectionPanel.querySelector<HTMLButtonElement>('.qb-plus')?.addEventListener('click', () => {
      this.quickBuildQty = Math.min(MAX_QUICK_BUILD_QTY, this.quickBuildQty + 1);
    });
    this.elSelectionPanel.querySelector<HTMLButtonElement>('.quick-build-btn')?.addEventListener('click', () => {
      this.doQuickBuild();
    });
    const combatIds = unitsSel.filter(isCombatCapable).map((u) => u.id);
    this.elSelectionPanel.querySelectorAll<HTMLButtonElement>('.stance-btn[data-stance]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (combatIds.length === 0) return;
        const stance = btn.dataset.stance as Stance;
        this.session.queueCommand({ type: 'setStance', unitIds: combatIds, stance });
      });
    });
    // Guard/Patrol: arm a targeting mode (session.pendingOrder); input.ts
    // collects the click(s) and issues the command for the current selection.
    // Toggle off if the same order is clicked again.
    this.elSelectionPanel.querySelectorAll<HTMLButtonElement>('.order-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (combatIds.length === 0) return;
        const kind = btn.dataset.order as 'guard' | 'patrol';
        if (this.session.pendingOrder?.kind === kind) {
          this.session.pendingOrder = null;
        } else {
          this.session.pendingOrder = kind === 'guard' ? { kind: 'guard' } : { kind: 'patrol', a: null };
        }
      });
    });
  }

  /** Quick Build: auto-places the currently-selected building near the
   *  player's HQ using the same findBuildSpot spiral-search the bot/copilot
   *  use, then issues the build command(s) immediately — no map tap. For
   *  qty > 1, each subsequent spot is found by fanning the search anchor out
   *  from the previous spot (mirroring the bot's own build-order chaining),
   *  since the client's own state snapshot doesn't yet reflect
   *  not-yet-simulated placements within the same batch. All n commands
   *  target the same engineer; the sim's existing assistNearbyConstruction
   *  chaining (see sim.ts) walks that engineer between the sites in turn. */
  private doQuickBuild(): void {
    const s = this.session.state;
    const placement = this.session.placement;
    if (!s || !placement) return;
    const me = this.session.myPlayer;
    const p = s.players[me];
    const bs = BUILDINGS[placement.building];
    const cost = Math.round(bs.costBudget * FACTIONS[p.faction].costMult);
    const qty = Math.min(Math.max(1, this.quickBuildQty), MAX_QUICK_BUILD_QTY);
    if (p.budget < cost * qty || p.data < bs.costData * qty) return; // stale click vs. disabled button

    const hq = playerBuildings(s, me).find((b) => b.type === 'hq');
    const near: Vec2 = hq
      ? entityCenter(hq)
      : { x: this.session.camera.x + 8, y: this.session.camera.y + 8 };
    const spots = this.pickQuickBuildSpots(s, near, bs.size, qty);
    for (const spot of spots) {
      this.session.queueCommand({
        type: 'build',
        unitId: placement.engineerId,
        building: placement.building,
        x: spot.x,
        y: spot.y,
      });
    }
    this.quickBuildQty = 1;
    this.quickBuildForBuilding = null;
    this.session.placement = null;
  }

  /** findBuildSpot only ever looks at committed sim state, so back-to-back
   *  calls in the same batch would happily return the same clear tile twice.
   *  Fan the search anchor out from each spot found so far to keep them
   *  distinct, same spirit as the bot's one-site-per-tick build order. */
  private pickQuickBuildSpots(s: GameState, near: Vec2, size: number, n: number): Vec2[] {
    const spots: Vec2[] = [];
    let anchor = near;
    for (let i = 0; i < n; i++) {
      let spot = findBuildSpot(s, anchor, size);
      let guard = 0;
      while (
        spot &&
        spots.some((sp) => Math.abs(sp.x - spot!.x) < size + 1 && Math.abs(sp.y - spot!.y) < size + 1) &&
        guard < 8
      ) {
        anchor = { x: spot.x + size + 2, y: spot.y };
        spot = findBuildSpot(s, anchor, size);
        guard++;
      }
      if (!spot) break;
      spots.push(spot);
      anchor = { x: spot.x + size + 2, y: spot.y };
    }
    return spots;
  }

  // -------------------------------------------------------------------
  // Scoreboard
  // -------------------------------------------------------------------

  private renderScoreboard(s: GameState): void {
    const canSeeFinancials = this.session.hasFocusAdoption();
    let rows = '<tr><th>Player</th><th>Faction</th><th>Phase</th><th>Revenue/s</th><th>Burn/s</th></tr>';
    s.players.forEach((p, i) => {
      const mine = i === this.session.myPlayer;
      const show = mine || canSeeFinancials;
      rows += `<tr class="${p.alive ? '' : 'eliminated'} ${mine ? 'me' : ''}">
        <td>${escapeHtml(p.name)}${p.isBot ? ' (Bot)' : ''}</td>
        <td>${FACTIONS[p.faction].name}</td>
        <td>${PHASE_NAMES[p.phase]}</td>
        <td>${show ? p.revenuePerSec.toFixed(1) : '??'}</td>
        <td>${show ? p.burnPerSec.toFixed(1) : '??'}</td>
      </tr>`;
    });
    this.elScoreboardTable.innerHTML = rows;
  }

  // -------------------------------------------------------------------
  // Game over
  // -------------------------------------------------------------------

  private updateGameOver(s: GameState): void {
    if (s.winner === null) {
      this.elGameOver.hidden = true;
      return;
    }
    this.elGameOver.hidden = false;
    // Achievements arrive a beat AFTER the overlay renders (the room relays
    // them once the leaderboard confirms the match report), so this section
    // updates independently of the one-shot stats render below.
    if (this.achShown !== this.session.newAchievements.length) {
      this.achShown = this.session.newAchievements.length;
      this.elGameOverAch.innerHTML =
        '<div class="gameover-ach-title">Achievements unlocked</div>' +
        this.session.newAchievements
          .map((id) => {
            const def = ACHIEVEMENTS[id];
            if (!def) return '';
            return `<div class="gameover-ach-row"><span class="ach-badge">${def.emoji}</span> <strong>${escapeHtml(
              def.name,
            )}</strong> <span class="gameover-ach-desc">${escapeHtml(def.desc)}</span></div>`;
          })
          .join('');
    }
    if (this.gameOverRendered) return;
    this.gameOverRendered = true;
    const won = s.winner === this.session.myPlayer;
    this.elGameOverTitle.textContent = s.winner === -1 ? 'Mutual Destruction' : won ? 'Victory' : 'Defeat';
    const secs = Math.floor(s.tick / TICKS_PER_SECOND);
    this.elGameOverStats.innerHTML = `<div>Match length: ${fmtClock(secs)}</div>`;
    // AoE-style post-game summary: metric graph + full stats table (summary.ts)
    renderSummary(this.elGameOverSummary, s, this.session.roomCode);
  }
}

function q(root: HTMLElement, sel: string): HTMLElement {
  const el = root.querySelector(sel);
  if (!el) throw new Error(`hud: missing element ${sel}`);
  return el as HTMLElement;
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
